import React from 'react';
import { ThumbsUp, ThumbsDown, Loader2 } from 'lucide-react';
import { createApi } from '../../lib/api';
import toast from 'react-hot-toast';

export default function BlogReactions({ slug, likes: initialLikes = 0, dislikes: initialDislikes = 0 }) {
  const [likes, setLikes] = React.useState(Number(initialLikes));
  const [dislikes, setDislikes] = React.useState(Number(initialDislikes));
  const [userVote, setUserVote] = React.useState(null); // 'like' | 'dislike' | null
  const [loading, setLoading] = React.useState(false);

  // Check local storage on mount
  React.useEffect(() => {
    const saved = localStorage.getItem(`blog_reaction_${slug}`);
    if (saved) setUserVote(saved);
  }, [slug]);

  const handleRate = async (type) => { // type: 'like' | 'dislike'
    if (loading || userVote) return;

    // Optimistic update
    if (type === 'like') setLikes(l => l + 1);
    else setDislikes(d => d + 1);
    
    setUserVote(type);
    setLoading(true);

    try {
      const api = createApi();
      await api.post(`/blog/posts/${slug}/react`, { action: type });
      localStorage.setItem(`blog_reaction_${slug}`, type);
      toast.success("Thanks for your feedback!");
    } catch (err) {
      // Revert
      if (type === 'like') setLikes(l => l - 1);
      else setDislikes(d => d - 1);
      setUserVote(null);
      toast.error("Failed to save reaction");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex items-center gap-6 py-6 border-t border-b border-white/5 my-8">
      <span className="text-sm font-semibold text-white/40 uppercase tracking-widest">Was this helpful?</span>
      
      <div className="flex gap-3">
        <button
          onClick={() => handleRate('like')}
          disabled={!!userVote || loading}
          className={`flex items-center gap-2 px-4 py-2 rounded-full border transition-all ${
            userVote === 'like'
              ? 'bg-green-500/10 border-green-500/50 text-green-400'
              : 'bg-white/5 border-white/10 text-white/60 hover:bg-white/10 hover:border-white/20'
          } disabled:opacity-50 disabled:cursor-not-allowed`}
        >
          {loading && userVote === 'like' ? <Loader2 size={16} className="animate-spin" /> : <ThumbsUp size={16} className={userVote === 'like' ? 'fill-current' : ''} />}
          <span className="font-mono text-sm">{likes}</span>
        </button>

        <button
          onClick={() => handleRate('dislike')}
          disabled={!!userVote || loading}
          className={`flex items-center gap-2 px-4 py-2 rounded-full border transition-all ${
            userVote === 'dislike'
              ? 'bg-red-500/10 border-red-500/50 text-red-400'
              : 'bg-white/5 border-white/10 text-white/60 hover:bg-white/10 hover:border-white/20'
          } disabled:opacity-50 disabled:cursor-not-allowed`}
        >
          {loading && userVote === 'dislike' ? <Loader2 size={16} className="animate-spin" /> : <ThumbsDown size={16} className={userVote === 'dislike' ? 'fill-current' : ''} />}
          <span className="font-mono text-sm">{dislikes}</span>
        </button>
      </div>
    </div>
  );
}
